import ChatSingleTag from "@/components/ChatSingleTag/chatSingleTag.component";
import ErrorBoundary from "@/components/ErrorBoundary/errorBoundary.component";
import TagsViewSectionEdit from "@/components/TagsView/Sections/TagsViewSectionEdit/tagsViewSectionEdit.component";
import TagsViewSectionMetrics from "@/components/TagsView/Sections/TagsViewSectionMetrics/tagsViewSectionMetrics.component";
import TagsViewSectionSelect from "@/components/TagsView/Sections/TagsViewSectionSelect/tagsViewSectionSelect.component";
import { ROUTE_TAGS } from "@/constants/routes.constants";
import { AppContext } from "@/services/AppContext/appContext";
import { Tag } from "@/types";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

type TagsViewProps = {
  // TODO: add props here
};

const TagsView: React.FC<TagsViewProps> = (props) => {
  const { tagID } = useParams();
  const nav = useNavigate();
  const [viewingTag, setViewingTag] = useState<Tag | null | "invalid">(null);
  const { userTags } = useContext(AppContext);
  const [searchParams] = useSearchParams();
  const [additionalTagIDs, setAdditionalTagIDs] = useState<number[]>([]);

  useEffect(() => {
    try {
      if (!userTags || userTags.length === 0) {
        return;
      }

      if (!tagID) {
        // Iterate through categories to find the first tag
        const firstTag = userTags
          .flatMap((category) => category.tags)
          .find((tag) => tag);

        if (firstTag) {
          nav(`${ROUTE_TAGS}/${firstTag.id}`);
        }
        return;
      }
      // check if number
      const tagIDNum = Number(tagID);
      if (isNaN(tagIDNum)) {
        console.error("Tag ID is not a number");
        setViewingTag("invalid");
        return;
      }

      // check if tagID exists in userTags
      const tag = userTags
        .flatMap((category) => category.tags)
        .find((tag) => tag.id === tagIDNum);
      if (!tag) {
        console.error("Tag ID does not exist in userTags");
        setViewingTag("invalid");
        return;
      }
      setViewingTag(tag);
    } catch (error) {
      console.error("Error in TagsViewSectionSelect", error);
      return;
    }
  }, [tagID, userTags, nav]);

  useEffect(() => {
    // Parse the 'additionalTags' parameter from the URL
    const additionalTagsParam = searchParams.get("additionalTags");
    // console.log(additionalTagsParam);
    if (additionalTagsParam) {
      const parsedIDs = additionalTagsParam
        .split(",")
        .map((id) => Number(id))
        .filter((id) => !isNaN(id)); // Ensure all parsed IDs are valid numbers
      setAdditionalTagIDs(parsedIDs);
    } else {
      setAdditionalTagIDs([]); // Reset if the parameter is not present
    }
  }, [searchParams]);

  return (
    <div className="flex flex-row gap-2 p-2 justify-between h-full w-full">
      <div className="w-[200px] 2xl:w-[250px] h-full select-none border rounded">
        <ErrorBoundary>
          <TagsViewSectionSelect
            viewingTagID={
              !viewingTag || viewingTag === "invalid" ? null : viewingTag.id
            }
            additionalTagIDs={additionalTagIDs}
          />
        </ErrorBoundary>
      </div>
      {!viewingTag ? null : viewingTag === "invalid" ? (
        <div className="flex flex-col gap-2 p-2 h-full w-full items-center justify-center">
          <div className="text-2xl font-bold">Tag not found</div>
        </div>
      ) : (
        <>
          <div className="w-[400px] 2xl:w-[600px] h-full">
            <ErrorBoundary key={viewingTag.id + "_viewingTag"}>
              <div className="flex flex-col gap-2 h-full">
                <TagsViewSectionEdit viewingTag={viewingTag} />
                <div className="grow overflow-hidden">
                  <ChatSingleTag
                    key={viewingTag.id + "_viewingTagChat"}
                    tagID={viewingTag.id}
                    additionalTagIDs={additionalTagIDs}
                  />
                </div>
              </div>
            </ErrorBoundary>
          </div>
          <div className="grow">
            <ErrorBoundary key={viewingTag.id + "_viewingTagMetrics"}>
              <div className="border h-full w-full p-2 rounded">
                <TagsViewSectionMetrics viewingTag={viewingTag} />
              </div>
            </ErrorBoundary>
          </div>
        </>
      )}
    </div>
  );
};

export default TagsView;
